import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ConditionalSkipSettings } from '@/components/admin/conditional-skip/ConditionalSkipSettings';
import { ConditionalSkipMonitor } from '@/components/admin/conditional-skip/ConditionalSkipMonitor';
import { ConditionalSkipHistory } from '@/components/admin/conditional-skip/ConditionalSkipHistory';
import { ConditionalSkipGuide } from '@/components/admin/conditional-skip/ConditionalSkipGuide';
import { StrategyGroupManager } from '@/components/admin/conditional-skip/StrategyGroupManager';
import { GroupAnalytics } from '@/components/admin/conditional-skip/GroupAnalytics';
import { GroupTimeline } from '@/components/admin/conditional-skip/GroupTimeline';
import { Type2SystemOverview } from '@/components/admin/conditional-skip/Type2SystemOverview';
import { Settings, Monitor, History, BookOpen, Users, BarChart3, Clock, TrendingUp } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';

const ConditionalSkipPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('settings');
  const isMobile = useIsMobile();

  return (
    <div className="min-h-screen bg-background">
      <div className={cn("container mx-auto", isMobile ? "py-4 px-0" : "py-8")}>
        <div className={cn("mb-6", isMobile && "px-4")}>
          <h1 className={cn("font-bold text-foreground mb-2", isMobile ? "text-2xl" : "text-3xl")}>
            Conditional Skip
          </h1>
          <p className="text-muted-foreground text-sm">
            Filter TradingView signals based on consecutive loss streaks per bot
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="inline-flex w-full overflow-x-auto pb-px">
            <TabsTrigger value="settings" className="flex items-center gap-2 flex-shrink-0">
              <Settings className="h-4 w-4" />
              {!isMobile && <span>Settings</span>}
            </TabsTrigger>
            <TabsTrigger value="groups" className="flex items-center gap-2 flex-shrink-0">
              <Users className="h-4 w-4" />
              {!isMobile && <span>Groups</span>}
            </TabsTrigger>
            <TabsTrigger value="monitor" className="flex items-center gap-2 flex-shrink-0">
              <Monitor className="h-4 w-4" />
              {!isMobile && <span>Monitor</span>}
            </TabsTrigger>
            <TabsTrigger value="type2-overview" className="flex items-center gap-2 flex-shrink-0">
              <TrendingUp className="h-4 w-4" />
              {!isMobile && <span>Type 2</span>}
            </TabsTrigger>
            <TabsTrigger value="analytics" className="flex items-center gap-2 flex-shrink-0">
              <BarChart3 className="h-4 w-4" />
              {!isMobile && <span>Analytics</span>}
            </TabsTrigger>
            <TabsTrigger value="timeline" className="flex items-center gap-2 flex-shrink-0">
              <Clock className="h-4 w-4" />
              {!isMobile && <span>Timeline</span>}
            </TabsTrigger>
            <TabsTrigger value="history" className="flex items-center gap-2 flex-shrink-0">
              <History className="h-4 w-4" />
              {!isMobile && <span>History</span>}
            </TabsTrigger>
            <TabsTrigger value="guide" className="flex items-center gap-2 flex-shrink-0">
              <BookOpen className="h-4 w-4" />
              {!isMobile && <span>Guide</span>}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="settings" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Bot Configuration</CardTitle>
                <CardDescription>
                  Configure conditional skip settings for each bot
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ConditionalSkipSettings />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="groups" className="mt-6">
            <StrategyGroupManager />
          </TabsContent>

          <TabsContent value="monitor" className="mt-6">
            <ConditionalSkipMonitor />
          </TabsContent>

          <TabsContent value="type2-overview" className="mt-6">
            <Type2SystemOverview />
          </TabsContent>

          <TabsContent value="analytics" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Group Analytics</CardTitle>
                <CardDescription>
                  Performance metrics and statistics for strategy groups
                </CardDescription>
              </CardHeader>
              <CardContent>
                <GroupAnalytics />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="timeline" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Group Timeline</CardTitle>
                <CardDescription>
                  Bot activity history and switching timeline
                </CardDescription>
              </CardHeader>
              <CardContent>
                <GroupTimeline />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="history" className="mt-6">
            <ConditionalSkipHistory />
          </TabsContent>

          <TabsContent value="guide" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Setup Guide</CardTitle>
                <CardDescription>
                  Learn how to configure and use Conditional Skip
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ConditionalSkipGuide />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default ConditionalSkipPage;